import { Select as BaseSelect } from '@base-ui/react/select';
import { cn } from '../lib/cn.js';
import { token } from '../theme/tokens.js';
import type { SelectOption } from './SearchableSelect.js';

export interface SelectProps {
  options: readonly SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /**
   * Accessible name for the trigger. This component renders no visible label
   * of its own — every current caller wraps it in its own `<label>Text<Select
   * /></label>` filter-chip layout, and a second, internal label would either
   * duplicate that text or force every caller into a layout only some of them
   * want. `aria-label` keeps the accessible name explicit regardless of
   * whether the wrapping `<label>` actually associates with a `<button>`.
   */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * `neutral` (default) matches every filter chip's own input styling.
   * `primary` matches the design system's solid-blue call-to-action buttons —
   * `MembersTab`'s Invite member and its Space Access drawer — for a
   * `Select` standing in as the trigger for one, rather than filtering
   * anything.
   */
  variant?: 'neutral' | 'primary';
}

const TRIGGER_STYLE: Record<'neutral' | 'primary', Record<string, string>> = {
  neutral: {
    borderColor: token('bp-border-strong'),
    background: token('bp-input'),
    color: token('bp-text'),
    outlineColor: token('bp-blue-rail'),
  },
  // `bp-on-accent` (white), not `bp-blue-text` — the readme's own line for
  // solid blue buttons, and the same fix every copy of `primaryButtonStyle()`
  // in this workspace already carries.
  primary: {
    borderColor: token('bp-blue'),
    background: token('bp-blue-deep'),
    color: token('bp-on-accent'),
    outlineColor: token('bp-blue-rail'),
  },
};

const ICON_COLOR: Record<'neutral' | 'primary', string> = {
  neutral: token('bp-text-muted'),
  primary: token('bp-on-accent'),
};

/**
 * A plain (non-searchable) select, replacing the native `<select>` this
 * workspace used to reach for directly.
 *
 * **Built on Base UI's `Select`, not styled native chrome.** A native
 * `<select>`'s closed control takes `radius-md` fine, but its open option list
 * is OS/browser chrome — no author stylesheet reaches its corners in any
 * engine. Base UI renders the popup itself, as ordinary DOM this component
 * fully controls, which is the only way to get `radius-md` on the open state
 * too.
 *
 * **The popup is portalled to `document.body` by default** — Base UI's own
 * behavior, not something this component adds — which sidesteps the
 * stacking-context trap `DetailDrawer`, `RowActionsMenu`, and
 * `SearchableSelect` all had to be fixed for or built around: a popup that
 * opens while an ancestor `bp-animate-fade-in` wrapper is mid-animation has no
 * ancestor left to be confined by. `z-50` on the positioner is deliberately
 * above `DetailDrawer`'s own `z-40`: both portal to `document.body` as
 * siblings, so a `Select` mounted inside a `DetailDrawer` (as
 * `SpaceAccessFieldset` is, from both `GrantEditor` and `InviteMemberForm`)
 * would otherwise open its popup *behind* the drawer's opaque panel — visible
 * in the DOM, unreachable to a click. `SearchableSelect`'s own popup is `z-20`
 * because it is never mounted inside a `DetailDrawer`; if that changes, it
 * needs the same fix this one just got.
 *
 * **Reuses `SearchableSelect`'s `SelectOption` type** rather than a second,
 * near-identical one — same shape, same two consumers' worth of reason to
 * exist as a real type.
 */
export function Select({
  options,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  className,
  variant = 'neutral',
}: SelectProps) {
  return (
    <BaseSelect.Root
      items={options.map((option) => ({ label: option.label, value: option.value }))}
      value={value}
      onValueChange={(next) => {
        if (typeof next === 'string') onChange(next);
      }}
      disabled={disabled}
    >
      <BaseSelect.Trigger
        aria-label={label}
        className={cn(
          'inline-flex min-h-9 items-center justify-between gap-2 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:outline-2 disabled:opacity-60',
          className,
        )}
        style={TRIGGER_STYLE[variant]}
      >
        <BaseSelect.Value placeholder={placeholder} />
        <BaseSelect.Icon className="shrink-0" style={{ color: ICON_COLOR[variant] }}>
          <ChevronIcon />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>

      <BaseSelect.Portal>
        {/*
          `alignItemWithTrigger={false}`: Base UI's default overlaps the popup
          over the trigger so the selected item's text lines up with the
          trigger's — the native `<select>` look. Every other popover in this
          workspace (`SearchableSelect`, `RowActionsMenu`) opens *below* its
          trigger instead, and matching that is more consistent here than
          matching native `<select>` would be.

          `align="end"`, `primary` only — left `undefined` for `neutral`, which
          keeps Base UI's own default (`center`) exactly as it was. A `primary`
          trigger stands in for a call-to-action button, and every current
          caller of one sits at the trailing edge of its row (`justify-end`) —
          a popup whose own trailing edge does not meet the trigger's reads as
          misaligned there, the same complaint a right-aligned button with a
          left-hanging menu would draw. A `neutral` trigger is an ordinary
          filter with no such complaint on record, and center is not this
          fix's to change.
        */}
        <BaseSelect.Positioner
          sideOffset={4}
          {...(variant === 'primary' ? { align: 'end' as const } : {})}
          className="z-50"
          alignItemWithTrigger={false}
        >
          <BaseSelect.Popup
            className="max-h-60 overflow-auto rounded-md border py-1 shadow-lg outline-none"
            style={{
              borderColor: token('bp-border'),
              background: token('bp-panel'),
              boxShadow: token('shadow-md'),
            }}
          >
            <BaseSelect.List>
              {options.map((option) => (
                <BaseSelect.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    'cursor-pointer whitespace-nowrap px-3 py-2 text-sm outline-none',
                    'data-[selected]:font-semibold',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                  )}
                  /*
                    **Both colours move together, and both move inline.**

                    `bp-on-accent` has to travel with the fill: `bp-blue-deep` is
                    a *solid* blue on the daylight palette — it has to be,
                    because fifteen buttons put white text on it — so a
                    highlighted option left carrying `bp-text` is near-black on
                    mid blue there. White on it reads 4.9:1 in daylight and
                    8.6:1 in the command room.

                    That pairing was already written once, as
                    `data-[highlighted]:text-…` beside an inline resting
                    `color` — and it never applied, because an inline style
                    outranks any class. Base UI takes `style` as a function of
                    item state, so the two values sit in one declaration that
                    nothing can outrank. `SearchableSelect` has the same
                    conditional for the same reason; it just tracks its own
                    `activeIndex` rather than being handed the state.

                    Base UI highlights the *selected* option the moment the
                    popup opens, so this is the resting appearance of an open
                    list, not only a hover.
                  */
                  style={(state) => ({
                    background: state.highlighted ? token('bp-blue-deep') : 'transparent',
                    color: state.highlighted ? token('bp-on-accent') : token('bp-text'),
                  })}
                >
                  <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}

/** Chevron-down, hand-copied like `FolderView`'s `MoreActionsIcon` — no icon-library dependency in this workspace. */
function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
